import type { Nullable } from "@shared-kernel/core/types/nullable";
import type { OBJECTIVE_ACQUISITION_STATUS } from "../training-follow-up";

// Un OBJECTIF du groupe : l'élève l'assigne, le conseiller lui donne un niveau d'acquisition. Il
// préexiste à l'évaluation. Un objectif abandonné n'arrive jamais jusqu'ici : la lecture l'écarte à
// la collecte, parce qu'il n'a jamais porté de note, ne peut plus en porter, et que la soumission le
// refuse. L'élève, lui, garde la trace de son abandon — c'est son acte, et son écran le montre.
export interface ObjectiveDetailVM {
  id: string;
  quantifiableObjective: string;
  acquisitionStatus: Nullable<OBJECTIVE_ACQUISITION_STATUS>;
  number: number; // Order number
  assignmentDate: string; // ISO date string
  isCustom: boolean; // true if custom objective created by student
}

// L'APPRÉCIATION rendue par un conseiller sur un élément de formation : le verdict écrit, qui porte
// sur la formation menée et non sur un objectif isolé. C'est la seule moitié de l'évaluation qui a
// un enregistrement à elle — l'autre vit sur les objectifs, dans leur statut d'acquisition.
export interface TrainingItemAppraisalVM {
  id: string;
  disclosedAppraisal: string; // Communiquée à l'élève
  privateAppraisal: Nullable<string>; // Interne, jamais servie à l'élève
  submittedAt: string; // ISO date string
}

// L'ÉVALUATION d'un élément de formation par un conseiller : ses objectifs notés PLUS son
// appréciation. Aucune table ne la porte — elle se lit comme la réunion des deux.
export interface TrainingItemEvaluation {
  objectives: ObjectiveDetailVM[];
  appraisal: Nullable<TrainingItemAppraisalVM>;
}

export interface ObjectiveRating {
  objectiveId: string;
  acquisitionStatus: OBJECTIVE_ACQUISITION_STATUS;
}

// Ce que vaut UNE soumission, quel que soit l'auteur : un INSTANTANÉ COMPLET du groupe. Elle porte
// tous les objectifs servis par la lecture, ni plus ni moins, et l'appréciation communiquée à
// l'élève est obligatoire — sans elle, le conseiller ne lui laisserait rien. Le backend rejette un
// envoi partiel. Le conseiller y ajoute (type, trainingItemId) ; le partenaire l'envoie tel quel.
export interface TrainingItemEvaluationPayload {
  objectives: ObjectiveRating[];
  disclosedAppraisal: string;
  privateAppraisal: Nullable<string>;
}

// L'ordre dans lequel l'élève a posé ses objectifs, et celui dans lequel ils se notent. Le numéro
// est servi pour ça ; on le rejoue plutôt que de se fier à l'ordre du tableau. La copie n'est pas
// cosmétique : ces objectifs viennent du store, et `sort` trie sur place.
export const sortObjectivesByNumber = (objectives: ObjectiveDetailVM[]): ObjectiveDetailVM[] =>
  [...objectives].sort((a, b) => a.number - b.number);

export const countUnratedObjectives = (objectives: ObjectiveDetailVM[]): number =>
  objectives.filter(objective => objective.acquisitionStatus === null).length;

// LA définition de « l'évaluation est rendue », et la seule : tous les objectifs portent un statut
// ET l'appréciation est déposée. Le backend compte ses actions en attente avec cette règle — la
// redériver ailleurs fait diverger l'écran du compteur. Le prédicat resserre le type au passage :
// une évaluation rendue porte forcément son appréciation, l'affichage n'a rien à réaffirmer.
export const isEvaluationSubmitted = <T extends TrainingItemEvaluation>(
  evaluation: T,
): evaluation is T & { appraisal: TrainingItemAppraisalVM } =>
  evaluation.appraisal !== null && countUnratedObjectives(evaluation.objectives) === 0;
